import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { installRoutePrefetch } from "@/utils/routePrefetch";

// === SYNC IMPORTS (kept in main bundle for fast FCP/LCP) ===
import Layout from "@/components/site/Layout";
import DevisLayout from "@/components/site/DevisLayout";
import ScrollToHash from "@/components/site/ScrollToHash";
import Home from "@/pages/Home";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";
import PartnerProtectedRoute from "@/components/admin/PartnerProtectedRoute";

// === ROUTE TABLES (lazy imports déclarés par domaine dans src/routes/) ===
import { SERVICE_ROUTES, DEPARTMENT_ROUTES, LEGAL_ROUTES } from "@/routes/serviceRoutes";
import { CITY_ROUTES } from "@/routes/cityRoutes";
import { ARRONDISSEMENT_ROUTES } from "@/routes/arrondissementRoutes";
import { LONG_DISTANCE_ROUTES } from "@/routes/longDistanceRoutes";
import { AOT_ROUTES } from "@/routes/aotRoutes";
import { DEVIS_STANDALONE_ROUTES, DEVIS_SUB_ROUTES } from "@/routes/devisRoutes";
import { LANDING_ROUTES } from "@/routes/landingRoutes";

// Admin (rarely loaded by public visitors)
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminDetail = lazy(() => import("@/pages/AdminDetail"));
const AdminStats = lazy(() => import("@/pages/AdminStats"));
const PartnerDashboard = lazy(() => import("@/pages/PartnerDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Toutes les pages publiques rendues dans le layout complet (header + footer)
const SITE_ROUTES = [
  ...SERVICE_ROUTES,
  ...DEPARTMENT_ROUTES,
  ...CITY_ROUTES,
  ...ARRONDISSEMENT_ROUTES,
  ...LONG_DISTANCE_ROUTES,
  ...LEGAL_ROUTES,
  ...AOT_ROUTES,
  ...DEVIS_SUB_ROUTES,
];

// Lightweight fallback while chunks load. Empty div keeps layout stable.
function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      aria-hidden="true"
    />
  );
}

function App() {
  useEffect(() => {
    // Install global hover / focus / touchstart prefetch on internal links.
    installRoutePrefetch();
    const onTelClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!a) return;
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/call`, {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source: a.getAttribute("data-testid") || "tel-link",
            page: window.location.pathname,
          }),
        }).catch(() => {});
      } catch (_) {
        // tracking must never block the call
      }
    };
    document.addEventListener("click", onTelClick, true);
    return () => document.removeEventListener("click", onTelClick, true);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <ScrollToHash />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/detail/:tab/:id"
                element={
                  <AdminProtectedRoute>
                    <AdminDetail />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/stats"
                element={
                  <AdminProtectedRoute>
                    <AdminStats />
                  </AdminProtectedRoute>
                }
              />

              {/* Espace partenaire (role="partner" uniquement) */}
              <Route
                path="/partenaire"
                element={
                  <PartnerProtectedRoute>
                    <PartnerDashboard />
                  </PartnerProtectedRoute>
                }
              />

              {/* Devis (layout minimal) */}
              {DEVIS_STANDALONE_ROUTES.map(({ path, Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <DevisLayout>
                      <Component />
                    </DevisLayout>
                  }
                />
              ))}

              {/* Landing pages Ads (layout autonome, noindex, hors sitemap) */}
              {LANDING_ROUTES.map(({ path, Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}

              {/* Reste du site (layout complet) */}
              <Route
                path="*"
                element={
                  <Layout>
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        {SITE_ROUTES.map(({ path, Component }) => (
                          <Route key={path} path={path} element={<Component />} />
                        ))}
                        <Route path="/demenagement-international" element={<Navigate to="/international" replace />} />

                        {/* Catch-all 404 — capture toutes les URL inconnues
                            (ex. anciennes URL WordPress /AAAA/MM/JJ/slug/, slugs supprimés…)
                            et sert une page noindex pour faire sortir Google de l'index. */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                }
              />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </div>
  );
}

export default App;
